import { match, matchPath } from 'react-router'
import { createSelector } from 'reselect'
import { selectInviteUserIdFromRoute } from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.selectors'
import { OrgInviteModel, OrgInviteResponse } from 'src/models/people.models'
import {
  PeopleCancelOrgInviteMatchParams,
  peopleCancelOrgInviteRouteProps,
  PeopleInvitedToOrgDetailMatchParams,
  peopleInvitedToOrgDetailRouteProps,
  PeopleMembersDetailMatchParams,
  peopleMembersDetailRouteProps,
} from 'src/navigation/routeProps'
import { RootState } from 'src/redux/createRootReducer'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'

export const selectInvitedToOrgListResponsesFromState = (
  state: RootState,
): Record<string, OrgInviteResponse> => state.data.invitedToOrg.listResponses

export const selectInvitedToOrgListModelsFromState = createSelector(
  [selectInvitedToOrgListResponsesFromState],
  (peopleListResponses) =>
    Object.values(peopleListResponses).reduce<
      Record<OrgInviteResponse['id'], OrgInviteModel>
    >(
      (acc, curr) => ({
        ...acc,
        [curr.id]: new OrgInviteModel(curr),
      }),
      {},
    ),
)

export const selectUserInviteFromState = createSelector(
  [selectInviteUserIdFromRoute, selectInvitedToOrgListResponsesFromState],
  (inviteUserId, orgInvites) =>
    Object.values(orgInvites).filter(
      (invite) => invite.invitee.id === inviteUserId,
    ),
)

export const selectInviteIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<PeopleInvitedToOrgDetailMatchParams>(
      location.pathname,
      peopleInvitedToOrgDetailRouteProps,
    ) as match<PeopleInvitedToOrgDetailMatchParams>
    return mtch?.params.id
  },
)

export const selectCancelInviteIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<PeopleCancelOrgInviteMatchParams>(
      location.pathname,
      peopleCancelOrgInviteRouteProps,
    ) as match<PeopleCancelOrgInviteMatchParams>
    return mtch?.params.id
  },
)

export const selectUserInviteFromRoute = createSelector(
  [selectInviteIdFromRoute, selectInvitedToOrgListResponsesFromState],
  (inviteId, orgInvites) => {
    return orgInvites[inviteId] !== undefined ? orgInvites[inviteId] : undefined
  },
)

export const selectCancelUserInviteFromRoute = createSelector(
  [selectCancelInviteIdFromRoute, selectInvitedToOrgListResponsesFromState],
  (inviteId, orgInvites) => {
    return orgInvites[inviteId] !== undefined ? orgInvites[inviteId] : undefined
  },
)

export const selectUserIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const membersMtch = matchPath<PeopleMembersDetailMatchParams>(
      location.pathname,
      peopleMembersDetailRouteProps,
    ) as match<PeopleMembersDetailMatchParams>
    const invitedToOrgMtch = matchPath<PeopleInvitedToOrgDetailMatchParams>(
      location.pathname,
      peopleInvitedToOrgDetailRouteProps,
    ) as match<PeopleInvitedToOrgDetailMatchParams>
    return membersMtch?.params.id || invitedToOrgMtch?.params.id
  },
)

export const selectUserInviteFromUserIdRoute = createSelector(
  [selectUserIdFromRoute, selectInvitedToOrgListResponsesFromState],
  (userId, orgInvites) =>
    orgInvites !== undefined
      ? Object.values(orgInvites).filter(
          (invite) => invite.invitee.id === userId,
        )[0]
      : undefined,
)
